import { getQualityPreset } from './CloudQuality.js';

export const CLOUD_PARAMETER_RANGES = Object.freeze( {
	coverage: [ 0.05, 0.95 ],
	density: [ 0.05, 3 ],
	shapeScale: [ 0.35, 4 ],
	softness: [ 0.01, 0.35 ],
	bottomFade: [ 0.01, 0.45 ],
	topFade: [ 0.02, 0.65 ],
	anvil: [ 0, 1 ],
	detailErosion: [ 0, 1.5 ],
	detailContrast: [ 0.25, 3 ],
	detailScale: [ 1, 8 ],
	worldScale: [ 400, 12000 ],
	weatherScale: [ 1000, 50000 ],
	weatherStrength: [ 0, 1.5 ],
	warpScale: [ 400, 20000 ],
	warpStrength: [ 0, 1.5 ],
	baseBlend: [ 0, 1 ],
	detailBlend: [ 0, 1 ],
	boundaryFade: [ 0.01, 0.35 ],
	detailFilter: [ 0, 1 ],
	extinction: [ 0.001, 0.12 ],
	sunIntensity: [ 0, 12 ],
	ambientIntensity: [ 0, 3 ],
	phaseForward: [ 0, 0.92 ],
	phaseBackward: [ - 0.8, 0 ],
	phaseMix: [ 0, 1 ],
	powder: [ 0, 4 ],
	windSpeed: [ 0, 1 ],
	turbulenceSpeed: [ 0, 1 ],
	formationStrength: [ 0, 1 ],
	formationSpeed: [ 0, 1 ],
	roilStrength: [ 0, 1.5 ],
	roilSpeed: [ 0, 2 ],
	viewSteps: [ 16, 220 ],
	lightSteps: [ 1, 12 ],
	detailOctaves: [ 1, 3 ],
	renderScale: [ 0.1, 1 ],
	jitterStrength: [ 0, 1 ],
	earlyExit: [ 0.001, 0.2 ],
	lightDistance: [ 0.01, 0.5 ],
	shadowDistribution: [ 1, 4 ]
} );

export const DEFAULT_CLOUD_PARAMETERS = Object.freeze( {
	quality: 'balanced',
	coverage: 0.29,
	density: 1,
	shapeScale: 1,
	softness: 0.085,
	bottomFade: 0.16,
	topFade: 0.32,
	anvil: 0.28,
	detailErosion: 0.72,
	detailContrast: 1.35,
	detailScale: 3.2,
	worldScale: 2600,
	weatherScale: 8500,
	weatherStrength: 0.62,
	warpScale: 4200,
	warpStrength: 0.42,
	baseBlend: 0.38,
	detailBlend: 0.32,
	boundaryFade: 0.12,
	detailFilter: 0.2,
	extinction: 0.023,
	sunAzimuth: 225,
	sunElevation: 18,
	sunIntensity: 1.45,
	sunColor: '#fff0d2',
	cloudColor: '#dfe9ee',
	ambientTop: '#759ab4',
	ambientBottom: '#26394b',
	ambientIntensity: 0.28,
	phaseForward: 0.72,
	phaseBackward: - 0.24,
	phaseMix: 0.78,
	powder: 0.76,
	windDirection: 32,
	windSpeed: 0.018,
	turbulenceSpeed: 0.006,
	formationStrength: 0.42,
	formationSpeed: 0.08,
	roilStrength: 0.38,
	roilSpeed: 0.26,
	viewSteps: 76,
	lightSteps: 4,
	detailOctaves: 2,
	renderScale: 0.72,
	jitterStrength: 0.9,
	animatedJitter: true,
	earlyExit: 0.015,
	lightDistance: 0.18,
	shadowDistribution: 2,
	debugMode: 0,
	frozen: false
} );

export function clampCloudParameters( parameters ) {

	const next = { ...parameters };

	for ( const [ key, range ] of Object.entries( CLOUD_PARAMETER_RANGES ) ) {

		if ( typeof next[ key ] === 'number' ) {

			next[ key ] = Math.min( range[ 1 ], Math.max( range[ 0 ], next[ key ] ) );

		}

	}

	next.viewSteps = Math.round( next.viewSteps );
	next.lightSteps = Math.round( next.lightSteps );
	next.detailOctaves = Math.round( next.detailOctaves );
	next.debugMode = Math.min( 3, Math.max( 0, Math.round( Number( next.debugMode ) || 0 ) ) );

	return next;

}

export function createCloudParameters( overrides = {} ) {

	const quality = overrides.quality ?? DEFAULT_CLOUD_PARAMETERS.quality;
	const preset = getQualityPreset( quality );

	return clampCloudParameters( {
		...DEFAULT_CLOUD_PARAMETERS,
		...preset,
		...overrides,
		quality
	} );

}

export function applyQualityToParameters( parameters, quality ) {

	return clampCloudParameters( {
		...parameters,
		...getQualityPreset( quality ),
		quality
	} );

}
